import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getBookingConfirmation, setBookingDoneStatus, setBookingStatus, unblockDate } from '../../../core/state/bookings/asyncActions';
import { BookingStatus } from '../../../core/enums/BookingStatus';
import Booking from '../../../core/types/model/Booking';
import { getBookingNotification, SubmitAction } from '../helpers';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import BookingCancellationModal from '../modals/booking-cancelation/BookingCancellationModal';
import BookingRejectionModal from '../modals/booking-rejection/BookingRejectionModal';
import BookingChangeStatusModal from '../modals/booking-change-status/BookingChangeStatusModal';
import { StateStatus } from '../../../core/enums/StateStatus';
import { getStateStatus } from '../../../core/state/bookings/selectors';
import { useInternalUser } from '../../../core/hooks/useInternalUser';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';
import { NotificationPosition } from '../../../core/state/global-notifications/types';
import { Actions } from '../../../core/state/bookings/actions';
import ActionDropdown from '../../../components/action-dropdown/ActionDropdown';
import { RootState } from '../../../core/state/state';
import { UserRoleType } from '../../../core/state/user/types';

interface Props {
  booking: Booking;
  fetchBookings?: () => void;
}

const BookingListDropdown: React.FC<Props> = ({ booking, fetchBookings }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const stateStatus = useSelector(getStateStatus);
  const isInternal = useInternalUser();
  const projectParticipant = useSelector((state: RootState) =>
    state.user.userInfo?.userRole.find((role) => role.name === UserRoleType.ProjectParticipant)
  );
  const [submitAction, setSubmitAction] = useState<SubmitAction>();
  const [isCancellationModalOpen, setCancellationModalOpen] = useState<boolean>(false);
  const [isRejectionModalOpen, setRejectionModalOpen] = useState<boolean>(false);
  const [isChangeToDoneModalOpen, setChangeToDoneModalOpen] = useState<boolean>(false);
  const [isChangeToOpenModalOpen, setChangeToOpenModalOpen] = useState<boolean>(false);

  const closeCancellationModal = useCallback(() => {
    setCancellationModalOpen(false);
  }, []);

  const cancelBooking = () => {
    setCancellationModalOpen(true);
  };

  const confirmBooking = async () => {
    setSubmitAction('approve');
    await dispatch(setBookingStatus(booking, BookingStatus.Open));
  };

  const unblockSlot = async () => {
    await dispatch(unblockDate(String(booking.id)));
    if (fetchBookings) {
      fetchBookings();
    }
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        postition: NotificationPosition.Top,
        message: t('projectSettings.blockBookingSlots.unblockDate.notification')
      })
    );
  };

  const changeBookingToDone = (bookingDoneAt?: string) => {
    if (!bookingDoneAt) return;
    dispatch(
      setBookingDoneStatus(booking, {
        status: BookingStatus.Done,
        bookingDoneAt
      })
    );
    setSubmitAction('done');
  };

  const changeBookingToOpen = () => {
    setSubmitAction('open');
    dispatch(setBookingStatus(booking, BookingStatus.Open));
  };

  useEffect(() => {
    if (submitAction && stateStatus === StateStatus.Loaded) {
      const notification = getBookingNotification(submitAction, t);
      if (notification) {
        dispatch(spawnGlobalNotification(notification));
      }
      setChangeToDoneModalOpen(false);
      setChangeToOpenModalOpen(false);
      setCancellationModalOpen(false);
    }
  }, [dispatch, stateStatus, submitAction, t]);

  const editBooking = useCallback(
    (data) => {
      dispatch(Actions.openEditModal());
      dispatch(Actions.openDetails(data));
    },
    [dispatch]
  );

  return (
    <>
      <ActionDropdown>
        {isInternal && !projectParticipant && (
          <>
            {booking.confirmationStatus === BookingStatus.Pending && (
              <Dropdown.Item onClick={() => setRejectionModalOpen(true)}>{t('booking.reject.button')}</Dropdown.Item>
            )}
            {booking.confirmationStatus === BookingStatus.Open && (
              <>
                <Dropdown.Item onClick={() => setChangeToDoneModalOpen(true)}>{t('booking.done.button')}</Dropdown.Item>
                <Dropdown.Item onClick={cancelBooking}>{t('booking.cancel.button')}</Dropdown.Item>
              </>
            )}
            {booking.confirmationStatus === BookingStatus.Done && (
              <Dropdown.Item onClick={() => setChangeToOpenModalOpen(true)}>{t('booking.open.button')}</Dropdown.Item>
            )}
            {booking.confirmationStatus === BookingStatus.Pending && (
              <Dropdown.Item onClick={confirmBooking}>{t('booking.approve.button')}</Dropdown.Item>
            )}
            {booking.confirmationStatus === BookingStatus.Blocked && (
              <Dropdown.Item onClick={unblockSlot}>{t('projectSettings.blockBookingSlots.button.unblock')}</Dropdown.Item>
            )}
          </>
        )}
        {booking.confirmationStatus === BookingStatus.Open && isInternal && (
          <Dropdown.Item onClick={() => editBooking(booking)}>{t('common.editBooking')}</Dropdown.Item>
        )}
        {booking.confirmationStatus !== BookingStatus.Blocked && !projectParticipant && (
          <Dropdown.Item onClick={() => dispatch(getBookingConfirmation(booking.id))}>{t('booking.confirmationFile.download')}</Dropdown.Item>
        )}
      </ActionDropdown>
      {isCancellationModalOpen && (
        <BookingCancellationModal
          booking={booking}
          isOpen={isCancellationModalOpen}
          closeModal={closeCancellationModal}
          onSubmit={() => {
            setSubmitAction('cancel');
          }}
        />
      )}
      {isRejectionModalOpen && (
        <BookingRejectionModal
          booking={booking}
          isOpen={isRejectionModalOpen}
          closeModal={() => setRejectionModalOpen(false)}
          onSubmit={() => {
            setSubmitAction('reject');
          }}
        />
      )}
      {isChangeToDoneModalOpen && (
        <BookingChangeStatusModal
          message="booking.changeToDone"
          isOpen={isChangeToDoneModalOpen}
          closeModal={() => setChangeToDoneModalOpen(false)}
          onConfirm={changeBookingToDone}
          showDateTimeFields
        />
      )}

      {isChangeToOpenModalOpen && (
        <BookingChangeStatusModal
          message="booking.changeToOpen"
          isOpen={isChangeToOpenModalOpen}
          closeModal={() => setChangeToOpenModalOpen(false)}
          onConfirm={() => {
            changeBookingToOpen();
          }}
        />
      )}
    </>
  );
};

export default BookingListDropdown;
